/**
 * Pattern Match (card flip) content: types, level params, deck selection, round builder.
 * Spec: face-down cards, tap two at a time to find matching pairs; content from backend decks or local fallback.
 */

export interface PatternMatchCard {
  /** Unique id for this slot (e.g. "0", "1") */
  id: string;
  /** Same for both cards in a pair; used to detect matches */
  pairId: string;
  imageUrl: string;
  label: string;
}

export interface RoundContent {
  cards: PatternMatchCard[];
}

export interface LevelParams {
  gridRows: number;
  gridCols: number;
  pairCount: number;
  timeSeconds: number;
  minCardSizePx: number;
}

/** Backend deck: one entry per deck in items array */
export interface PatternMatchDeck {
  deckId?: string;
  cards?: { cardId?: string; imageUrl?: string; label?: string }[];
}

function isDeck(x: unknown): x is PatternMatchDeck {
  if (!x || typeof x !== "object") return false;
  const d = x as Record<string, unknown>;
  return Array.isArray(d.cards) && d.cards.length > 0;
}

/** Seeded RNG in [0, 1) for deterministic rounds */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

function shuffleWithSeed<T>(arr: T[], rng: () => number): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Spec levels: 1–2 2×3 (3 pairs), 3–4 3×4 (6), 5–6 4×4 (8) 60s, 7–8 4×5 (10) 90s, 9–10 4×6 (12) 120s */
export function getLevelParams(level: number): LevelParams {
  const clamped = Math.max(1, Math.min(10, level));
  if (clamped <= 2) {
    return {
      gridRows: 2,
      gridCols: 3,
      pairCount: 3,
      timeSeconds: 0,
      minCardSizePx: 90,
    };
  }
  if (clamped <= 4) {
    return {
      gridRows: 3,
      gridCols: 4,
      pairCount: 6,
      timeSeconds: 0,
      minCardSizePx: 80,
    };
  }
  if (clamped <= 6) {
    return {
      gridRows: 4,
      gridCols: 4,
      pairCount: 8,
      timeSeconds: 68,
      minCardSizePx: 80,
    };
  }
  if (clamped <= 8) {
    return {
      gridRows: 4,
      gridCols: 5,
      pairCount: 10,
      timeSeconds: 100,
      minCardSizePx: 80,
    };
  }
  return {
    gridRows: 4,
    gridCols: 6,
    pairCount: 12,
    timeSeconds: 132,
    minCardSizePx: 80,
  };
}

/** Local fallback: minimal deck so game always loads. Uses public images. */
const FALLBACK_CARDS: { cardId: string; category: string; label: string }[] = [
  { cardId: "cat", category: "animals", label: "Cat" },
  { cardId: "dog", category: "animals", label: "Dog" },
  { cardId: "bird", category: "animals", label: "Bird" },
  { cardId: "fish", category: "animals", label: "Fish" },
  { cardId: "rabbit", category: "animals", label: "Rabbit" },
  { cardId: "horse", category: "animals", label: "Horse" },
  { cardId: "apple", category: "food", label: "Apple" },
  { cardId: "bread", category: "food", label: "Bread" },
  { cardId: "chair", category: "household", label: "Chair" },
  { cardId: "book", category: "household", label: "Book" },
  { cardId: "sun", category: "nature", label: "Sun" },
  { cardId: "star", category: "symbols", label: "Star" },
];

function getFallbackCards(): { cardId: string; imageUrl: string; label: string }[] {
  const base = process.env.PUBLIC_URL || "";
  return FALLBACK_CARDS.map(({ cardId, category, label }) => ({
    cardId,
    imageUrl: `${base}/games/images/${category}/${cardId}.png`,
    label,
  }));
}

/** Build a single fallback image path by category */
function fallbackImageUrl(cardId: string): string {
  const base = process.env.PUBLIC_URL || "";
  const entry = FALLBACK_CARDS.find((c) => c.cardId === cardId);
  const category = entry?.category ?? "animals";
  return `${base}/games/images/${category}/${cardId}.png`;
}

function buildRoundFromDeckCards(
  deckCards: { cardId: string; imageUrl: string; label: string }[],
  pairCount: number,
  seed: string,
  roundIndex: number
): RoundContent {
  const take = deckCards.slice(0, pairCount);
  const pairs: PatternMatchCard[] = [];
  take.forEach((c, i) => {
    const pairId = c.cardId || `p${i}`;
    const idA = `slot-${i}-a`;
    const idB = `slot-${i}-b`;
    const imageUrl = c.imageUrl || fallbackImageUrl(c.cardId || "");
    const label = c.label || c.cardId || "";
    pairs.push({ id: idA, pairId, imageUrl, label });
    pairs.push({ id: idB, pairId, imageUrl, label });
  });
  const rng = seededRandom(`${seed}-${roundIndex}`);
  const shuffled = shuffleWithSeed(pairs, rng);
  return { cards: shuffled };
}

function isPatternMatchContentResponse(
  data: unknown
): data is { items?: unknown[] } {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  if (!Array.isArray(d.items)) return false;
  return d.items.some((item) => isDeck(item));
}

/**
 * Resolve round content: use backend decks if valid and enough cards; else local fallback.
 */
export function resolveRoundContent(
  contentSeed: string,
  roundIndex: number,
  levelParams: LevelParams,
  backendData: unknown,
  backendError: unknown,
  sessionSeed?: string
): RoundContent {
  const effectiveSeed = sessionSeed
    ? (contentSeed ? `${contentSeed}-${sessionSeed}` : sessionSeed)
    : contentSeed || `local-${roundIndex}-${Date.now()}`;

  if (
    !backendError &&
    isPatternMatchContentResponse(backendData) &&
    Array.isArray((backendData as { items: unknown[] }).items)
  ) {
    const items = (backendData as { items: unknown[] }).items;
    const decks = items.filter(isDeck);
    if (decks.length > 0) {
      const seedNum =
        parseInt(effectiveSeed.slice(0, 4), 16) || roundIndex * 17;
      const deck = decks[seedNum % decks.length];
      const cards = deck?.cards ?? [];
      const normalized = cards.map((c) => ({
        cardId: (c?.cardId ?? "").toString(),
        imageUrl: (c?.imageUrl ?? "").toString(),
        label: (c?.label ?? "").toString(),
      }));
      if (normalized.length >= levelParams.pairCount) {
        return buildRoundFromDeckCards(
          normalized,
          levelParams.pairCount,
          effectiveSeed,
          roundIndex
        );
      }
    }
  }

  const fallback = getFallbackCards();
  const pairCount = Math.min(levelParams.pairCount, Math.floor(fallback.length));
  return buildRoundFromDeckCards(
    fallback.slice(0, pairCount),
    pairCount,
    effectiveSeed,
    roundIndex
  );
}

/**
 * Per-round scoring for Pattern Match.
 *
 * A "move" is one attempt (two cards flipped). Accuracy is the share of
 * moves that produced a correct pair; `errorCount` is the count of wrong
 * pair attempts. If no moves are recorded we return 0 accuracy to avoid a
 * divide-by-zero.
 */
export interface PatternMatchRoundInput {
  totalMoves: number;
  wrongMatches: number;
}

export interface PatternMatchRoundMetrics {
  accuracy: number;
  correctMatches: number;
  errorCount: number;
}

export function computePatternMatchRoundMetrics(
  input: PatternMatchRoundInput,
): PatternMatchRoundMetrics {
  const { totalMoves, wrongMatches } = input;
  const correctMatches = Math.max(0, totalMoves - wrongMatches);
  const accuracy = totalMoves > 0 ? correctMatches / totalMoves : 0;
  return { accuracy, correctMatches, errorCount: wrongMatches };
}
